// Os indicadores de solução usam uma família por município; horas continuam
// pertencendo aos lançamentos individuais e não são multiplicadas pela proposta.
export function basesPorMunicipio(rows) {
  const bases = new Map();
  rows.forEach(row => {
    const chave = `${row.municipioId}:${row.solucaoChave}`;
    if (!bases.has(chave)) bases.set(chave, { proposto: false, executado: false, cadastrado: false, interrompido: false });
    const base = bases.get(chave);
    base.proposto = base.proposto || row.proposto;
    base.executado = base.executado || row.executado;
    base.cadastrado = base.cadastrado || row.cadastrado;
    base.interrompido = base.interrompido || row.interrompido || row.situacao === "Interrompida";
  });
  return [...bases.values()];
}

export function indicadoresRadar(rows) {
  const bases = basesPorMunicipio(rows);
  const executadas = bases.filter(b => b.executado);
  const propostas = bases.filter(b => b.proposto);
  const aderentes = executadas.filter(b => b.proposto).length;
  const propostasExecutadas = propostas.filter(b => b.executado).length;
  return {
    totalBases: bases.length,
    totalPropostas: propostas.length,
    propostasExecutadas,
    semCadastro: propostas.filter(b => !b.cadastrado).length,
    propostasCadastradasSemExecucao: propostas.filter(b => b.cadastrado && !b.executado).length,
    propostasSemExecucao: propostas.length - propostasExecutadas,
    foraProposta: executadas.length - aderentes,
    aderenciaPct: executadas.length ? 100 * aderentes / executadas.length : null,
    coberturaPct: propostas.length ? 100 * propostasExecutadas / propostas.length : null,
  };
}

export function situacoesRadar(rows) {
  const grupos = [
    { id: "aderente", nome: "Com execução aderente", cor: "#12a37f" },
    { id: "proposta-sem-execucao", nome: "Solução prevista na proposta, cadastrada e sem execução", cor: "#e6a23c" },
    { id: "sem-cadastro", nome: "Solução prevista na proposta, sem cadastro no município e sem execução", cor: "#087d91" },
    { id: "fora-proposta", nome: "Executada sem proposta de referência", cor: "#e55b55" },
    { id: "cadastro-sem-execucao", nome: "Cadastrada sem proposta e sem execução", cor: "#78909c" },
  ].map(g => ({ ...g, qtd: 0, interrompidas: 0 }));
  const porId = new Map(grupos.map(g => [g.id, g]));
  basesPorMunicipio(rows).forEach(base => {
    const id = base.executado ? (base.proposto ? "aderente" : "fora-proposta")
      : base.proposto ? (base.cadastrado ? "proposta-sem-execucao" : "sem-cadastro")
      : "cadastro-sem-execucao";
    const grupo = porId.get(id);
    grupo.qtd++;
    // Interrupção é um detalhe do grupo, não outra parcela da contagem.
    if (base.interrompido) grupo.interrompidas++;
  });
  return grupos;
}

export function opcoesSolucoes(rows) {
  const nomes = new Map(rows.map(i => [i.solucaoChave, i.solucaoRaiz]));
  return [...nomes].map(([value, text]) => ({ value, text }))
    .sort((a, b) => a.text.localeCompare(b.text, "pt-BR"));
}

// Mesma regra da listagem de municípios: elegibilidade IAN é independente do status.
export function correspondeStatusMunicipio(item, statusId, statusSelecionado) {
  if (statusId === null || statusId === undefined || statusId === "") return true;
  const nome = (statusSelecionado?.statusNome || "").toString().normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "").toLowerCase();
  if (nome === "elegivel ian") return (item.elegivelIan ?? "").toString().toLowerCase() === "sim";
  return item.statusMunicipioId === statusId;
}

export function planoAcaoDisponivel(municipiosSelecionados) {
  return Array.isArray(municipiosSelecionados) && municipiosSelecionados.length === 1;
}

const STATUS_PLANO = {
  aderente: { nome: "Prevista e executada", cor: "#17875f" },
  "prevista-sem-execucao": { nome: "Prevista e não executada", cor: "#d99018" },
  "fora-proposta": { nome: "Executada sem proposta", cor: "#d9534f" },
  "cadastro-fora-proposta": { nome: "Fora da proposta e sem execução", cor: "#407ba7" },
};

function dataCivil(value) {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return { ano: Number(match[1]), mes: Number(match[2]) };
  const data = new Date(value);
  return Number.isNaN(data.getTime()) ? null : { ano: data.getFullYear(), mes: data.getMonth() + 1 };
}

function adicionarIntervalo(destino, inicio, fim) {
  const a = dataCivil(inicio), b = dataCivil(fim);
  if (!a || !b || a.ano * 12 + a.mes > b.ano * 12 + b.mes) return;
  for (let ano = a.ano, mes = a.mes; ano * 12 + mes <= b.ano * 12 + b.mes; mes++) {
    if (mes === 13) { ano++; mes = 1; }
    destino.add(`${ano}-${String(mes).padStart(2, "0")}`);
  }
}

function adicionarDataEncontro(base, value) {
  const data = dataCivil(value);
  if (!data) return;
  const mes = `${data.ano}-${String(data.mes).padStart(2, "0")}`;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const dataFormatada = match ? `${match[3]}/${match[2]}/${match[1]}` : String(value);
  base.mesesExecucao.add(mes);
  base.mesesReferencia.add(mes);
  if (!base.datasExecucao.has(mes)) base.datasExecucao.set(mes, new Set());
  base.datasExecucao.get(mes).add(dataFormatada);
}

export function montarPlanosAcao(rows, anoPadrao = new Date().getFullYear()) {
  const bases = new Map();
  rows.forEach(row => {
    const chave = `${row.municipioId}:${row.solucaoChave}`;
    if (!bases.has(chave)) bases.set(chave, {
      chave, municipioId: row.municipioId, municipio: row.municipio,
      regional: row.unidadeRegional, grupo: row.grupoSolucao || "Sem eixo",
      solucao: row.solucaoRaiz || row.solucao, proposto: false, executado: false,
      cadastrado: false, horasPrevistas: 0, horasExecutadas: 0,
      mesesExecucao: new Set(), mesesReferencia: new Set(), datasExecucao: new Map(),
    });
    const base = bases.get(chave);
    base.proposto = base.proposto || !!row.proposto;
    base.executado = base.executado || !!row.executado;
    base.cadastrado = base.cadastrado || !!row.cadastrado;
    base.horasPrevistas += Number(row.horasPrevistas || 0);
    base.horasExecutadas += Number(row.horasExecutadas || 0);
    adicionarIntervalo(base.mesesReferencia, row.propostaInicio, row.propostaFim);
    if (row.executado && row.cadastrado) {
      (row.datasEncontrosAgendados || []).forEach(data => adicionarDataEncontro(base, data));
    }
  });

  const municipios = new Map();
  bases.forEach(base => {
    const statusId = base.executado ? (base.proposto ? "aderente" : "fora-proposta")
      : base.proposto ? "prevista-sem-execucao" : "cadastro-fora-proposta";
    const status = STATUS_PLANO[statusId];
    const percentualExecucao = base.horasPrevistas > 0 ? 100 * base.horasExecutadas / base.horasPrevistas : null;
    const item = { ...base, percentualExecucao, statusId, status: status.nome, cor: status.cor };
    if (!municipios.has(base.municipioId)) municipios.set(base.municipioId, {
      id: base.municipioId, nome: base.municipio, regional: base.regional, itens: [], meses: new Set(),
    });
    const municipio = municipios.get(base.municipioId);
    item.mesesReferencia.forEach(m => municipio.meses.add(m));
    municipio.itens.push(item);
  });

  const porTexto = (a, b) => String(a).localeCompare(String(b), "pt-BR", { sensitivity: "base" });
  return [...municipios.values()].map(municipio => {
    municipio.itens.sort((a, b) => porTexto(a.grupo, b.grupo) || porTexto(a.solucao, b.solucao));
    const anos = [...municipio.meses].map(m => Number(m.slice(0, 4)));
    const unicos = anos.length ? [...new Set(anos)].sort((a, b) => a - b) : [anoPadrao];
    return { ...municipio, anos: unicos.map(ano => ({ ano, meses: Array.from({ length: 12 }, (_, i) => `${ano}-${String(i + 1).padStart(2, "0")}`) })) };
  }).sort((a, b) => porTexto(a.nome, b.nome));
}

export function legendaPlanoAcao() {
  return Object.entries(STATUS_PLANO).map(([id, item]) => ({ id, ...item }));
}
